/**
 * Connection Profiles — the control-plane view of shared provider credentials.
 *
 * WHAT THIS IS. A Connection Profile is a set of Cluvik-owned provider
 * credentials that a non-Live tenant can be given at provisioning time, so an
 * operator does not repeat Connection -> credentials -> validate -> activate for
 * every demo tenant. Until now the only way to see one was to create a tenant
 * with Sample Credentials on and read the card on its detail page: the profile
 * itself, which is the thing being reused, had no screen.
 *
 * READ-ONLY, ON PURPOSE. The Platform exposes import and validate; both write,
 * one of them takes secret material, and neither belongs in the first version of
 * a screen whose job is to answer "what would this tenant get, and does it
 * work". Test and Import land next, with confirmation and audit.
 *
 * IT SHOWS SECRET DESCRIPTIONS, NEVER SECRETS. The API already reduces a
 * reference to {present, backend, name}; this renders the backend and stops.
 *
 * WHY THE ENVIRONMENT PICKER HAS NO "LIVE". Sample credentials never attach to a
 * Live tenant — the Platform refuses it, the Admin route refuses it, and
 * offering the option so the server can say no is a control that exists to be
 * rejected.
 *
 * GET /api/admin/sample-connection-profiles?environment=... (tenant:read)
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/table';
import StatusBadge from '../../../components/admin/StatusBadge';
import EmptyState from '../../../components/admin/EmptyState';
import { Loader2, RefreshCw, AlertTriangle } from 'lucide-react';
import IdentityMark from '../../../design-system/identity/IdentityMark';
import ProviderMark from '../../../design-system/identity/ProviderMark';
import {
  PROFILE_ENVIRONMENTS, describeProvider, describeSecret, describeUnavailable,
  enabledProviders, summarizeProfile,
} from './connectionProfilePresentation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Health -> the StatusBadge tone key. Reuses the sample_* tones already in
 *  TONE_MAP rather than adding a second visual language for the same facts. */
const HEALTH_BADGE = {
  passing: 'sample_connected',
  degraded: 'sample_degraded',
  failing: 'sample_failed',
  unmeasured: 'sample_pending',
};

const formatWhen = (iso) => {
  if (!iso) return 'Never';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? 'Never' : d.toLocaleString();
};

export default function ConnectionProfilesPage() {
  const [environment, setEnvironment] = useState(PROFILE_ENVIRONMENTS[0]);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (env) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/admin/sample-connection-profiles`,
        {
          params: { environment: env },
          headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
        },
      );
      setPayload(data);
    } catch (e) {
      // A failed request is not an empty catalogue. Saying "no profiles" here
      // would be the same defect this page exists to expose in the data.
      setError(e.response?.data?.detail || e.message || 'Request failed');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(environment); }, [environment, load]);

  const profiles = payload?.profiles || [];
  const unavailable = describeUnavailable(payload);

  return (
    <div className="space-y-6" data-testid="connection-profiles-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        {/* Identity at identity-lg. The header is a PLACE, so it gets artwork;
            Refresh beside it is an action and stays a flat monochrome glyph.
            No accessible name on the mark — the h1 is right there, and
            announcing the same thing twice is worse than silence. */}
        <div className="flex items-start gap-3">
          <IdentityMark path="/connections/profiles" size="lg" className="mt-0.5 shrink-0" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Connection Profiles</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Cluvik-owned provider credentials a non-Live tenant can be provisioned with.
            A profile existing does not make a provider connected — only a live check does.
          </p>
        </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-border p-0.5" role="group" aria-label="Environment">
            {PROFILE_ENVIRONMENTS.map((env) => (
              <button
                key={env}
                type="button"
                onClick={() => setEnvironment(env)}
                aria-pressed={environment === env}
                data-testid={`env-${env}`}
                className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                  environment === env
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {env.charAt(0) + env.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => load(environment)} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            Refresh
          </Button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground" data-testid="profiles-loading">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading profiles for {environment.toLowerCase()}…
        </div>
      )}

      {!loading && error && (
        <Card className="border-destructive-text/30" data-testid="profiles-error">
          <CardContent className="flex items-start gap-3 py-6">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive-text" aria-hidden="true" />
            <div>
              <p className="text-sm font-medium text-foreground">Could not load profiles</p>
              <p className="mt-1 text-sm text-muted-foreground">{String(error)}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && !error && unavailable && (
        <Card className="border-warning/30" data-testid="profiles-unavailable">
          <CardContent className="flex items-start gap-3 py-6">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning-text" aria-hidden="true" />
            <div>
              <p className="text-sm font-medium text-foreground">
                The {environment.toLowerCase()} runtime could not be reached
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {unavailable}. This is not the same as having no profiles — nothing was measured.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && !error && !unavailable && profiles.length === 0 && (
        <Card>
          <CardContent>
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <IdentityMark path="/connections/profiles" size="2xl" />
              <p className="text-base font-medium text-foreground">
                No connection profile for {environment.toLowerCase()}
              </p>
              <p className="max-w-md text-sm text-muted-foreground">
                A profile is created the first time credentials are imported for this
                environment.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && !error && profiles.map((profile) => {
        const providers = enabledProviders(profile);
        return (
          <Card key={profile.id} data-testid={`profile-${profile.id}`}>
            <CardHeader>
              <CardTitle className="text-base">{profile.label || profile.id}</CardTitle>
              <CardDescription>
                <span className="font-mono">{profile.id}</span>
                {' · '}
                <span data-testid={`profile-summary-${profile.id}`}>{summarizeProfile(profile)}</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              {providers.length === 0 ? (
                <EmptyState
                  compact
                  title="No providers configured in this profile"
                  description="A tenant provisioned with this profile would receive no connections."
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Provider</TableHead>
                      {/* Two columns, not one chip: "not configured" and
                          "failing" call for opposite actions. */}
                      <TableHead>Setup</TableHead>
                      <TableHead>Health</TableHead>
                      <TableHead>Secret</TableHead>
                      <TableHead>Last checked</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {providers.map((p) => {
                      const d = describeProvider(p);
                      return (
                        <TableRow key={p.key} data-testid={`provider-${p.key}`}>
                          <TableCell>
                            {/* THE PROVIDER'S MARK. This page is the catalogue
                                view of the same three providers the tenant
                                screens show, and it was the one place naming a
                                provider with no mark beside it — so the same
                                provider looked like two different kinds of
                                thing depending on which screen you arrived
                                from. Same module, same identity-sm size, same
                                decorative role: the label is in the next
                                element and announcing it twice is worse than
                                announcing it once. */}
                            <div className="flex min-w-0 items-start gap-2.5">
                              <ProviderMark
                                provider={p.key}
                                label={p.label}
                                size="sm"
                                className="mt-0.5 shrink-0"
                              />
                              <div className="min-w-0">
                                <span className="font-medium text-foreground">{p.label}</span>
                                {p.config?.account_email && (
                                  <span className="ml-2 text-xs text-muted-foreground">{p.config.account_email}</span>
                                )}
                                {p.last_error && (
                                  <p className="mt-0.5 text-xs text-destructive-text">{p.last_error}</p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{d.lifecycleLabel}</TableCell>
                          <TableCell>
                            <StatusBadge status={HEALTH_BADGE[d.health]} label={d.healthLabel} />
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{describeSecret(p.secret)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatWhen(p.last_validated_at)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
